import { getStrapiToken, getFreshStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/modules/supplychain-suppliers.js

/**
 * SupplychainSuppliersHandler
 * 
 * Manages supply chain supplier data with enterprise-grade features:
 * - Permission-aware data loading with optimized caching strategy
 * - Intelligent refresh handling for different update types
 * - Real-time supplier property updates with immutable state patterns
 * - Invitation and reminder systems with optimistic UI updates
 * - Cross-module coordination for consistent data state
 * - Fallback mechanisms for API endpoints and error recovery
 */
export class SupplychainSuppliersHandler {
  /**
   * Initialize the supplychain suppliers handler with a store reference
   * @param {Object} store - The main application store instance
   */
  constructor(store) {
    this.store = store;
    this.isLoading = false;
    this.lastFetchTime = 0;
    this._loadingPromise = null; // ✅ Track current loading promise for concurrent call handling

    // Cache validity period (5 minutes)
    this.cacheMaxAge = 5 * 60 * 1000;

    // Track last update timestamps by action type for selective updates
    this.lastUpdatesByType = {
      invite: 0,
      remind: 0,
      reminderAll: 0,
      update: 0
    };

    // Operation Lightsaber: Smart invalidation tracking
    this.dataFreshness = {
      isStale: false,
      staleReason: null,
      lastValidation: null
    };

    // Background loading progress tracking
    this.loadingProgress = {
      isLoading: false,
      loaded: 0,
      total: 0
    };

    // Setup cross-tab synchronization for staleness
    if (typeof window !== 'undefined' && window.BroadcastChannel) {
      this.staleChannel = new BroadcastChannel('scm-staleness');
      this.staleChannel.onmessage = (event) => {
        if (event.data?.type === 'STALE') {
          this.dataFreshness.isStale = true;
          this.dataFreshness.staleReason = event.data.reason;
          // Cross-tab staleness logging silenced
        }
      };
    }

    // The list was blanked because access looked denied; load it for real
    // once the permissions say otherwise.
    if (typeof window !== 'undefined') {
      window.addEventListener('scm-permissions-changed', () => {
        if (this.store.state.data.supplychainSuppliers?.meta?.permissionStatus?.restricted) {
          this.loadSupplychainSuppliers().catch(() => {});
        }
      });
    }
  }

  /**
   * Mark data as stale to trigger refresh on next access
   * @param {string} reason - Reason for marking data stale
   * @param {boolean} triggerImmediate - Whether to notify subscribers immediately
   */
  markStale(reason, triggerImmediate = false) {
    this.dataFreshness.isStale = true;
    this.dataFreshness.staleReason = reason;
    this.dataFreshness.lastValidation = Date.now();

    // Broadcast to other tabs
    if (this.staleChannel) {
      this.staleChannel.postMessage({ type: 'STALE', reason, triggerImmediate });
    }

    // Notify subscribers immediately if requested
    if (triggerImmediate) {
      this.store.notifySubscribers('supplychainSuppliers:stale', { reason });
    }
  }

  /**
   * Clear staleness flag after loading fresh data
   */
  clearStale() {
    this.dataFreshness.isStale = false;
    this.dataFreshness.staleReason = null;
    this.dataFreshness.lastValidation = Date.now();
  }

  /**
   * Load supplychain suppliers data with permission-aware caching
   * @param {boolean} forceRefresh - Force refresh regardless of cache status
   * @returns {Promise<Array>} Array of supplier objects
   */
  async loadSupplychainSuppliers(forceRefresh = false) {
    try {
      // If already loading, wait for current load to complete
      // This prevents race conditions where multiple loads skip each other
      if (this.isLoading) {
        // Wait for current load to complete
        if (this._loadingPromise) {
          await this._loadingPromise;
        }
        // Return the freshly loaded data
        return this.store.state.data.supplychainSuppliers?.list || [];
      }

      // Set loading state
      this.store.state.loading.supplychainSuppliers = true;
      this.isLoading = true;

      // ✅ Store the loading promise so concurrent calls can wait on it
      this._loadingPromise = (async () => {
        try {
          // Check SCM permissions first
          let scmPermissionsService;
          try {
            const scmModule = await import('@/services/scm-permissions');
            scmPermissionsService = scmModule.scmPermissionsService;
          } catch (error) {
            // If we can't import permissions, assume we have access
            if (process.env.NODE_ENV === 'development') {
              console.warn('Could not import SCM permissions service:', error);
            }
          }

          // Check access permission if service is available AND initialized
          // On a fresh tab, permissions may not be ready yet — don't block data loading
          if (scmPermissionsService && scmPermissionsService.initialized && !scmPermissionsService.canAccessSupplyChain()) {
            // No access - return structured empty data without API call
            this._updateStore([], performance.now(), null, {
              restricted: true,
              reason: 'no_access',
              canAccess: false
            });
            // Not a fetch: the "no access" can be a fallback after a 401, and
            // caching this empty list showed "No suppliers invited yet" for
            // the whole cache age once access came back.
            this.lastFetchTime = 0;
            return [];
          }

          // ✅ FIX: Smart decision - check if data was FETCHED, not if list has items
          // This correctly treats "0 suppliers" as valid cached data
          const hasFetched = this.lastFetchTime > 0;

          if (!forceRefresh && hasFetched && !this.dataFreshness.isStale) {
            const cacheAge = Date.now() - this.lastFetchTime;
            if (cacheAge < this.cacheMaxAge) {
              // Using cached data - return list (may be empty for 0 suppliers)
              return this.store.state.data.supplychainSuppliers?.list || [];
            }
          }
          // Staleness reason logging silenced

          // Fetch fresh data
          const result = await this._fetchSuppliersData(forceRefresh);

          // Clear staleness after successful fetch
          this.clearStale();

          return result;
        } finally {
          // Always reset loading state
          this.isLoading = false;
          this._loadingPromise = null;
          this.store.state.loading.supplychainSuppliers = false;
        }
      })();

      return this._loadingPromise;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load supplychain suppliers:', error);
      }
      this.store.handleError('supplychainSuppliers', error);

      // Reset loading state
      this.isLoading = false;
      this._loadingPromise = null;
      this.store.state.loading.supplychainSuppliers = false;

      // Return existing data or empty array instead of throwing
      return this.store.state.data.supplychainSuppliers?.list || [];
    }
  }

  /**
   * Server-side search augment.
   *
   * While the full list is still background-loading, the table's client-side
   * filter can only match suppliers already streamed into the browser. This
   * fetches rows matching `query` from the backend (?search=) and MERGES them
   * into the in-memory list (append + dedupe by linkingId) so search finds any
   * supplier without waiting for the whole list to load.
   *
   * Invariant: the list only ever GROWS — never replaced or narrowed — and
   * loadingProgress is left untouched. So export (gated on loadingProgress),
   * select-all and counts (which all read this same list) cannot regress.
   * The merge is silent (no sessionStorage write of a large list) followed by
   * an explicit data-subscriber notify so the table re-renders the matches now
   * (a silent _updateStore otherwise repaints only at backgroundComplete).
   *
   * @param {string} query - search text (matched against name / supplierId / email)
   * @returns {Promise<number>} count of new rows merged in
   */
  async searchSuppliers(query) {
    const q = (query || '').trim();
    if (!q) return 0;

    const projectId = this.store.getProjectId();
    if (!projectId) return 0;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/supplychain-suppliers/project/${projectId}?search=${encodeURIComponent(q)}&limit=200`,
        {
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`,
            'Cache-Control': 'max-age=0'
          },
          signal: controller.signal
        }
      );
      clearTimeout(timeoutId);

      if (!response.ok) return 0;

      const payload = await response.json();
      const matches = Array.isArray(payload?.data) ? payload.data : [];
      if (matches.length === 0) return 0;

      // Append + dedupe by linkingId — same contract as the background chunk
      // loader. The list can only grow.
      const currentList = this.store.state.data.supplychainSuppliers?.list || [];
      const seenLinkingIds = new Set(currentList.map((s) => s.linkingId).filter((id) => id != null));
      const fresh = matches.filter((s) => s.linkingId == null || !seenLinkingIds.has(s.linkingId));
      if (fresh.length === 0) return 0;

      const newList = [...currentList, ...fresh];
      // silent=true: skip the costly saveState of a large list...
      this._updateStore(newList, performance.now(), null, null, true);
      // ...but DO notify data subscribers so the table renders the matches now.
      this.store.notifySubscribers('supplychainSuppliers');

      return fresh.length;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[SCM Search] server-side search failed:', error?.message || error);
      }
      return 0;
    }
  }

  /**
   * Fetch suppliers data from API with optimized error handling
   * Operation Lightsaber: Support for chunked loading
   * @private
   * @param {boolean} forceRefresh - Whether to bypass cache
   * @returns {Promise<Array>} Array of supplier objects
   */
  async _fetchSuppliersData(forceRefresh = false) {
    const projectId = this.store.getProjectId();
    if (!projectId) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('No project ID available when loading supplychain suppliers');
      }
      return [];
    }

    const startTime = performance.now();

    // Operation Lightsaber: Get first chunk immediately
    const firstChunk = await this._fetchChunk(projectId, 0, 100, forceRefresh);
    const totalCount = firstChunk.meta?.total || firstChunk.data?.length || 0;

    // Initialize loading progress tracking
    this.loadingProgress = {
      isLoading: totalCount > 100,
      loaded: firstChunk.data?.length || 0,
      total: totalCount
    };

    // A failed request is not "no suppliers": keep the list we already show
    // instead of replacing it with an empty one (ticket #1028).
    if (firstChunk.failed && this.lastFetchTime > 0) {
      console.warn('[SCM Load] Supplier list refresh failed; keeping the current list');
      return this.store.state.data.supplychainSuppliers?.list || [];
    }

    // Update store with first chunk for immediate display
    this._updateStore(firstChunk.data || [], startTime);

    // Notify subscribers of initial progress (so UI can show total immediately)
    this.store.notifySubscribers('supplychainSuppliers:loadingProgress', { ...this.loadingProgress });

    // If more data exists, load in background
    if (totalCount > 100) {
      this._loadRemainingInBackground(projectId, 100, totalCount, forceRefresh);
    }

    return firstChunk.data || [];
  }

  /**
   * Operation Lightsaber: Fetch a single chunk of supplier data
   * Includes retry logic for resilience against network hiccups and population timing
   * @private
   */
  async _fetchChunk(projectId, offset, limit, forceRefresh = false, retryCount = 0) {
    const MAX_RETRIES = 3; // Increased from 2 to handle population timing issues
    const RETRY_DELAY_BASE = 1000; // 1 second base delay, increases with retries
    const FETCH_TIMEOUT = 15000; // 15 second timeout per request

    try {
      // Add timeout to fetch request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/supplychain-suppliers/project/${projectId}?offset=${offset}&limit=${limit}`,
        {
          headers: {
            // Retries renew the token first: a stale one would fail the same way.
            Authorization: `Bearer ${retryCount > 0 ? await getFreshStrapiToken() : getStrapiToken()}`,
            'Cache-Control': forceRefresh ? 'no-cache' : 'max-age=0'
          },
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        // 404 = endpoint not found, 400 = no data for project (first login edge case)
        // Both cases: return empty data gracefully without retrying
        if (response.status === 404 || response.status === 400) {
          return { data: [], meta: { total: 0, hasMore: false } };
        }
        throw new Error(`Failed to fetch chunk: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      // Retry on network errors or timeouts
      if (retryCount < MAX_RETRIES) {
        const retryDelay = RETRY_DELAY_BASE * (retryCount + 1);
        console.warn(`Chunk fetch failed at offset ${offset}, retrying in ${retryDelay}ms (${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this._fetchChunk(projectId, offset, limit, forceRefresh, retryCount + 1);
      }

      console.error(`Error fetching chunk at offset ${offset} after ${MAX_RETRIES} retries:`, error);
      return { data: [], meta: { total: 0, hasMore: false }, failed: true };
    }
  }

  /**
   * Operation Lightsaber: Load remaining data in background
   * Includes retry queue for failed chunks to ensure all data is loaded
   * @private
   */
  async _loadRemainingInBackground(projectId, startOffset, totalCount, forceRefresh = false) {
    let offset = startOffset;
    const chunkSize = 1000; // Increased from 500 for faster loading
    const failedChunks = []; // Track chunks that failed all retries

    // ✅ FIX: Track if we're in background loading to prevent animation replays
    this.isBackgroundLoading = true;

    console.log(`[SCM Load] Starting background load: ${totalCount} total, starting at offset ${startOffset}`);

    while (offset < totalCount) {
      // Check if navigation changed (abort if user left SCM)
      if (!this._shouldContinueLoading()) break;

      const chunk = await this._fetchChunk(projectId, offset, chunkSize, forceRefresh);

      if (chunk.data && chunk.data.length > 0) {
        // Append to existing data, deduping by linkingId. ORDER BY updated_at
        // on the backend has no stable tiebreaker, so paginated chunks can
        // return the same row twice (e.g. right after a populate, when many
        // rows share a timestamp). Without dedup, the list grows duplicates
        // and select-all (Set) vs breakdown (Array) counts diverge.
        const currentList = this.store.state.data.supplychainSuppliers?.list || [];
        const seenLinkingIds = new Set(currentList.map((s) => s.linkingId).filter((id) => id != null));
        const dedupedChunk = chunk.data.filter((s) => s.linkingId == null || !seenLinkingIds.has(s.linkingId));
        const newList = [...currentList, ...dedupedChunk];

        // ✅ FIX: Update store silently (no saveState) for background chunks
        // silent=true prevents quota exceeded errors and unnecessary saves
        this._updateStore(newList, performance.now(), null, null, true);

        // Update loading progress and notify subscribers
        this.loadingProgress = {
          isLoading: true,
          loaded: newList.length,
          total: totalCount
        };
        this.store.notifySubscribers('supplychainSuppliers:loadingProgress', { ...this.loadingProgress });

        const dropped = chunk.data.length - dedupedChunk.length;
        console.log(`[SCM Load] Chunk loaded: offset=${offset}, got ${chunk.data.length}, deduped ${dropped}, total now ${newList.length}`);
      } else {
        // Chunk failed even after retries - track it for later
        console.warn(`[SCM Load] Chunk FAILED: offset=${offset}, will retry later`);
        failedChunks.push(offset);
      }

      offset += chunkSize;

      // Delay between chunks to avoid overwhelming backend (reduced from 1000ms)
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Retry failed chunks once more with longer delay
    if (failedChunks.length > 0) {
      console.log(`[SCM Load] Retrying ${failedChunks.length} failed chunks...`);
      for (const failedOffset of failedChunks) {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Longer delay for retries
        const chunk = await this._fetchChunk(projectId, failedOffset, chunkSize, forceRefresh);

        if (chunk.data && chunk.data.length > 0) {
          // Same dedup-on-append as the main loop above — retries can also
          // produce duplicates when chunk boundaries shift between attempts.
          const currentList = this.store.state.data.supplychainSuppliers?.list || [];
          const seenLinkingIds = new Set(currentList.map((s) => s.linkingId).filter((id) => id != null));
          const dedupedChunk = chunk.data.filter((s) => s.linkingId == null || !seenLinkingIds.has(s.linkingId));
          const newList = [...currentList, ...dedupedChunk];
          this._updateStore(newList, performance.now(), null, null, true);

          // Update progress after retry success
          this.loadingProgress.loaded = newList.length;
          this.store.notifySubscribers('supplychainSuppliers:loadingProgress', { ...this.loadingProgress });

          const dropped = chunk.data.length - dedupedChunk.length;
          console.log(`[SCM Load] Retry SUCCESS: offset=${failedOffset}, got ${chunk.data.length}, deduped ${dropped}`);
        } else {
          console.error(`[SCM Load] Retry FAILED: offset=${failedOffset} - data permanently lost`);
        }
      }
    }

    // ✅ FIX: Clear background loading flag
    this.isBackgroundLoading = false;

    // Mark loading as complete
    const finalCount = this.store.state.data.supplychainSuppliers?.list?.length || 0;
    this.loadingProgress = {
      isLoading: false,
      loaded: finalCount,
      total: totalCount
    };
    this.store.notifySubscribers('supplychainSuppliers:loadingProgress', { ...this.loadingProgress });
    this.store.notifySubscribers('supplychainSuppliers:backgroundComplete', { loaded: finalCount, total: totalCount });

    console.log(`[SCM Load] Background loading complete: ${finalCount}/${totalCount} loaded`);
  }

  /**
   * Operation Lightsaber: Check if we should continue loading chunks
   * ✅ FIX: Always continue loading - we want fresh data ready when user navigates
   * The background loading is non-blocking, so it doesn't hurt to continue even
   * if user is on a different page.
   * @private
   */
  _shouldContinueLoading() {
    // ✅ Always continue - preloading data improves UX when user navigates to SCM
    return true;
  }

  /**
   * Force populate suppliers data directly from API
   * @param {boolean} useFallback - Whether to use fallback endpoint if primary fails
   * @returns {Promise<boolean>} Success status
   */
  async forcePopulateFromAPI(useFallback = false) {
    const projectId = this.store.getProjectId();
    if (!projectId) return false;

    try {
      // Force populate logging silenced

      // Build API path based on fallback parameter
      const apiPath = useFallback ?
        'supplychain-suppliers/requester' :
        'supplychain-suppliers';

      // Build full URL
      const url = `${process.env.GATSBY_STRAPI_URL}/api/${apiPath}/project/${projectId}/populate`;

      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      try {
        // Call population endpoint
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify({
            force: true,
            immediate: true,
            critical: true,
            requesterProjectId: projectId,
            projectId: projectId
          }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          // For 404 errors with no fallback already tried, retry with fallback
          if (response.status === 404 && !useFallback) {
            return this.forcePopulateFromAPI(true);
          }

          throw new Error(`Population failed: ${response.status}`);
        }

        // Reload data after population
        await this.loadSupplychainSuppliers(true);
        return true;
      } catch (fetchError) {
        clearTimeout(timeoutId);

        // Handle abort error specifically
        if (fetchError.name === 'AbortError') {
          if (process.env.NODE_ENV === 'development') {
            console.warn(`Population request timed out after 8 seconds`);
          }

          // If timeout without fallback, try fallback
          if (!useFallback) {
            return this.forcePopulateFromAPI(true);
          }
        }

        throw fetchError;
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error(`Error populating suppliers:`, error);
      }
      return false;
    }
  }

  /**
   * Update store with suppliers data using immutable pattern
   * @private
   * @param {Array} suppliers - Suppliers data array
   * @param {number} startTime - Request start time for performance tracking
   * @param {string} errorMessage - Optional error message
   * @param {Object} permissionStatus - Optional permission status object
   * @param {boolean} silent - If true, don't notify subscribers (for background chunk updates)
   */
  _updateStore(suppliers, startTime, errorMessage = null, permissionStatus = null, silent = false) {
    const loadTime = Math.round(performance.now() - startTime);

    this.store.state.data.supplychainSuppliers = {
      list: suppliers,
      meta: {
        lastSync: new Date().toISOString(),
        lastAccess: new Date().toISOString(),
        count: suppliers.length,
        loadTime,
        error: errorMessage,
        permissionStatus
      }
    };

    this.lastFetchTime = Date.now();

    // For silent updates (background chunks), DON'T save to sessionStorage
    // Reasons:
    // 1. Suppliers are excluded from cache anyway (too large, can be 50MB+)
    // 2. Calling saveState() on every chunk was causing quota exceeded errors
    // 3. Subscribers still get notified on final update
    if (silent) {
      // Background chunk - just update in-memory state, no save needed
    } else {
      // Normal update - save state and notify subscribers
      this.store.saveState();
      this.store.notifySubscribers('supplychainSuppliers');
    }
  }

  /**
   * Update supplier properties directly in store with optimistic UI updates
   * @param {number|string} linkingId - Supplier linkingId to update
   * @param {Object} properties - Properties to update on the supplier
   * @returns {boolean} Success status
   */
  updateSupplierProperties(linkingId, properties) {
    try {
      const currentData = this.store.state.data.supplychainSuppliers;
      if (!currentData?.list?.length) return false;

      // Find supplier index
      const index = currentData.list.findIndex(s =>
        String(s.linkingId) === String(linkingId)
      );

      if (index === -1) return false;

      // Create a new list with the updated supplier
      const newList = [...currentData.list];
      newList[index] = {
        ...newList[index],
        ...properties
      };

      // Update the store
      this.store.state.data.supplychainSuppliers = {
        ...currentData,
        list: newList,
        meta: {
          ...currentData.meta,
          lastUpdate: new Date().toISOString()
        }
      };

      // Notify subscribers
      this.store.notifySubscribers('supplychainSuppliers');

      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error updating supplier properties:', error);
      }
      return false;
    }
  }

  /**
   * Fetch and update specific suppliers by linkingIds (Operation Lightsaber - targeted updates)
   * Instead of reloading ALL suppliers, this fetches only the changed ones from Strapi
   * and merges them into the existing list
   *
   * @param {number[]} linkingIds - Array of linkingIds to fetch
   * @returns {Promise<boolean>} Success status
   */
  async fetchAndUpdateByLinkingIds(linkingIds) {
    if (!linkingIds || !Array.isArray(linkingIds) || linkingIds.length === 0) {
      console.warn('[SCM] fetchAndUpdateByLinkingIds: No linkingIds provided');
      return false;
    }

    const projectId = this.store.getProjectId();
    if (!projectId) {
      console.warn('[SCM] fetchAndUpdateByLinkingIds: No project ID available');
      return false;
    }

    try {
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/supplychain-suppliers/project/${projectId}/by-linking-ids`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify({ linkingIds })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch suppliers by linkingIds: ${response.status}`);
      }

      const result = await response.json();
      const freshSuppliers = result.data || [];

      if (freshSuppliers.length === 0) {
        return true; // No suppliers to update
      }

      // Merge fresh suppliers into existing list
      const currentData = this.store.state.data.supplychainSuppliers;
      if (!currentData?.list) {
        // No existing data, just set the new suppliers
        this._updateStore(freshSuppliers, performance.now());
        return true;
      }

      // Create a map for fast lookup
      const freshMap = new Map(freshSuppliers.map(s => [s.linkingId, s]));

      // Update existing suppliers or add new ones
      const newList = currentData.list.map(supplier => {
        const fresh = freshMap.get(supplier.linkingId);
        if (fresh) {
          freshMap.delete(supplier.linkingId); // Mark as processed
          return fresh; // Replace with fresh data
        }
        return supplier; // Keep existing
      });

      // Add any new suppliers that weren't in the list
      for (const [, supplier] of freshMap) {
        newList.push(supplier);
      }

      // Update store with merged list
      this.store.state.data.supplychainSuppliers = {
        ...currentData,
        list: newList,
        meta: {
          ...currentData.meta,
          lastSync: new Date().toISOString(),
          lastTargetedUpdate: new Date().toISOString()
        }
      };

      // Notify subscribers
      this.store.notifySubscribers('supplychainSuppliers');

      return true;
    } catch (error) {
      console.error('[SCM] Error in fetchAndUpdateByLinkingIds:', error);
      // Fall back to full reload on error
      return false;
    }
  }

  /**
   * Invite a supplier to join the supply chain
   * @param {Object} supplierData - Data for the new supplier
   * @param {string} supplierData.email - Supplier contact email (required)
   * @param {string} supplierData.name - Supplier company name
   * @param {string} [supplierData.country] - Supplier country code
   * @param {string} [supplierData.contactName] - Supplier contact person name
   * @returns {Promise<Object>} Result of the invitation
   * @throws {Error} If API call fails or parameters are invalid
   */
  async inviteSupplier(supplierData) {
    try {
      const projectId = this.store.getProjectId();
      if (!projectId) {
        throw new Error('No project ID available');
      }

      // Invite supplier logging silenced

      // Set timestamp to avoid duplicate refreshes
      this.lastUpdatesByType.invite = Date.now();

      // Call the API
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/supplychain-suppliers/project/${projectId}/invite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify(supplierData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to invite supplier');
      }

      const result = await response.json();
      // Invitation success logging silenced

      // ✅ Operation Lightsaber: Instant UI update using returned supplier data
      // Backend now returns full supplier record - add directly to store
      if (result.data?.supplier) {
        const currentData = this.store.state.data.supplychainSuppliers;
        const currentList = currentData?.list || [];

        // Add new supplier to the beginning of the list (most recent first)
        const newList = [result.data.supplier, ...currentList];

        this.store.state.data.supplychainSuppliers = {
          ...currentData,
          list: newList,
          meta: {
            ...currentData?.meta,
            count: newList.length,
            lastSync: new Date().toISOString()
          }
        };

        // Notify subscribers for immediate UI update
        this.store.notifySubscribers('supplychainSuppliers');

        if (process.env.NODE_ENV === 'development') {
          console.log('[SCM] Instant UI update: Added new supplier to store');
        }
      } else {
        // Fallback: Mark as stale if no supplier data returned
        this.markStale('Supplier invited');
        this.loadSupplychainSuppliers(true);
      }

      return result.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to invite supplier:', error);
      }
      throw error;
    }
  }

  /**
   * Send reminder to a single supplier
   * @param {number} linkingId - Supplier linkingId to remind
   * @returns {Promise<Object>} API response data
   * @throws {Error} If API call fails or parameters are invalid
   */
  async remindSupplier(linkingId) {
    try {
      const projectId = this.store.getProjectId();
      if (!projectId) {
        throw new Error('No project ID available');
      }

      if (!linkingId) {
        throw new Error('Supplier linkage ID is required');
      }

      // Reminder logging silenced

      // Set timestamp to avoid duplicate refreshes
      this.lastUpdatesByType.remind = Date.now();

      // Call API
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/supplychain-suppliers/project/${projectId}/remind`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify({ linkingId })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Failed to send reminder');
      }

      const responseData = await response.json();

      // Check for spam protection warning (reminder was blocked)
      if (responseData.data?.warning && responseData.data?.warningCode === 'SPAM_PROTECTION') {
        if (process.env.NODE_ENV === 'development') {
          console.log('Reminder blocked by spam protection');
        }
        // Return warning response - do NOT update supplier data
        return responseData.data;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Reminder sent successfully');
      }

      // Get timestamp from response or use current time
      const timestamp = responseData.data?.timestamp || new Date().toISOString();

      // ✅ Operation Lightsaber: Instant UI update - backend already updated Strapi
      // Update supplier in store immediately for responsive UI
      // Also increment reminderCount optimistically (Snowflake increments it on successful reminder)
      const currentSupplier = this.store.state.data.supplychainSuppliers?.list?.find(
        s => String(s.linkingId) === String(linkingId)
      );
      const currentReminderCount = currentSupplier?.reminderCount || 0;

      this.updateSupplierProperties(linkingId, {
        lastLinkingRequest: timestamp,
        reminderCount: currentReminderCount + 1
      });

      // No need to reload - optimistic update is sufficient
      // Backend already synced to Strapi before returning
      if (process.env.NODE_ENV === 'development') {
        console.log('[SCM Reminder] Instant UI update applied, reminderCount incremented');
      }

      return responseData.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to send reminder:', error);
      }
      this.store.handleError('supplychainSuppliers', error);
      throw error;
    }
  }

  /**
   * Send bulk reminders to multiple suppliers
   * @param {Object} data - Reminder configuration
   * @param {string} [data.actionCode] - Action code ('REMIND_PARTICIPATION' or 'REQUEST_UPDATE')
   * @param {number[]} [data.linkingIds] - Array of supplier linkingIds to remind
   * @returns {Promise<Object>} API response data
   * @throws {Error} If API call fails or parameters are invalid
   */
  async remindAllSuppliers(data) {
    try {
      const projectId = this.store.getProjectId();
      if (!projectId) {
        throw new Error('No project ID available');
      }

      // Validate input
      if (!data.actionCode && !data.linkingIds?.length) {
        throw new Error('Either actionCode or linkingIds are required');
      }

      // Set timestamp to avoid duplicate refreshes
      this.lastUpdatesByType.reminderAll = Date.now();

      // Prepare request payload
      const payload = {};
      if (data.actionCode) {
        payload.actionCode = data.actionCode;
        // Bulk reminder action logging silenced
      } else {
        payload.linkingIds = data.linkingIds;
        // Bulk reminder count logging silenced
      }

      // Call API
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/supplychain-suppliers/project/${projectId}/remind-all`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Failed to send reminders');
      }

      const responseData = await response.json();

      // ✅ Operation Lightsaber: Instant UI update - backend already updated Strapi
      // Get affected IDs from either:
      // 1. Request data (linkingIds we sent)
      // 2. Response data (affected/affectedIds from backend)
      const affectedLinkingIds = data.linkingIds?.length
        ? data.linkingIds
        : (responseData.data?.affected || responseData.data?.affectedIds || []);

      // Check if this was a spam-protected warning (no actual reminders sent)
      const isSpamWarning = responseData.data?.warning && responseData.data?.warningCode === 'SPAM_PROTECTION';

      // Update UI immediately with optimistic updates if we have affected IDs and not blocked
      if (affectedLinkingIds.length > 0 && !isSpamWarning) {
        const timestamp = new Date().toISOString();

        affectedLinkingIds.forEach(linkingId => {
          const currentSupplier = this.store.state.data.supplychainSuppliers?.list?.find(
            s => String(s.linkingId) === String(linkingId)
          );
          const currentReminderCount = currentSupplier?.reminderCount || 0;

          this.updateSupplierProperties(linkingId, {
            lastLinkingRequest: timestamp,
            reminderCount: currentReminderCount + 1
          });
        });
      }

      return responseData.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to send bulk reminders:', error);
      }
      this.store.handleError('supplychainSuppliers', error);
      throw error;
    }
  }

  /**
   * Compatibility method for SupplierDetails drawer
   * @param {Object} supplier - Supplier object with linkingId property
   * @returns {Promise<Object>} API response
   * @throws {Error} If supplier object lacks required properties
   */
  async reminderSupplier(supplier) {
    const linkingId = supplier?.linkingId;
    if (!linkingId) {
      throw new Error('Cannot send reminder - supplier linkingId is missing');
    }

    return this.remindSupplier(linkingId);
  }
}